// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { WithId } from '@medplum/core';
import type { Schedule } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type {
  AppointmentTypeDefinition,
  AvailabilityTemplateEntry,
  DayOfWeekCode,
  ProviderRoleCode,
} from '@polarissleep/scheduling';
import {
  DAY_OF_WEEK_CODES,
  getEffectiveAvailabilityForWeek,
  getScheduleTimezone,
  getWeekStartForDate,
  readGeneralAvailability,
  reconcileScheduleAppointmentTypeAvailability,
  setGeneralAvailability,
  setSpecificAvailabilityOverride,
} from '@polarissleep/scheduling';
import { useCallback, useEffect, useState } from 'react';
import { type AvailabilityBlock, type AvailabilityMode, minutesToHHMM } from './types';

interface UseAvailabilityEditorStateParams {
  schedule: WithId<Schedule> | undefined;
  mode: AvailabilityMode;
  weekStart: Date;
  providerRoleCode: ProviderRoleCode | undefined;
  isAdmin: boolean;
  appointmentTypes: AppointmentTypeDefinition[];
  timezone: string | undefined;
}

interface UseAvailabilityEditorStateResult {
  blocks: AvailabilityBlock[];
  loading: boolean;
  saving: boolean;
  refresh: () => void;
  save: (nextBlocks: AvailabilityBlock[]) => Promise<void>;
}

function newBlockId(): string {
  return `block-${Math.random().toString(36).slice(2, 10)}`;
}

function templateToBlocks(entries: AvailabilityTemplateEntry[]): AvailabilityBlock[] {
  return entries.map((entry) => {
    const dayIndex = DAY_OF_WEEK_CODES.indexOf(entry.dayOfWeek);
    const [sh, sm] = entry.startTime.split(':').map(Number);
    const [eh, em] = entry.endTime.split(':').map(Number);
    return {
      id: newBlockId(),
      dayIndex,
      startMinutes: sh * 60 + sm,
      endMinutes: eh * 60 + em,
    };
  });
}

function blocksToTemplate(blocks: AvailabilityBlock[]): AvailabilityTemplateEntry[] {
  return blocks.map((block) => ({
    dayOfWeek: DAY_OF_WEEK_CODES[block.dayIndex] as DayOfWeekCode,
    startTime: minutesToHHMM(block.startMinutes),
    endTime: minutesToHHMM(block.endMinutes),
  }));
}

export function useAvailabilityEditorState({
  schedule,
  mode,
  weekStart,
  providerRoleCode,
  isAdmin,
  appointmentTypes,
  timezone,
}: UseAvailabilityEditorStateParams): UseAvailabilityEditorStateResult {
  const medplum = useMedplum();
  const scheduleTimezone = getScheduleTimezone(timezone);
  const weekStartTime = weekStart.getTime();
  const weekStartDate = getWeekStartForDate(weekStart, scheduleTimezone);
  const [blocks, setBlocks] = useState<AvailabilityBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey is a manual trigger to reload after save
  useEffect(() => {
    if (!schedule) {
      return;
    }

    const currentSchedule = schedule;
    let cancelled = false;
    setLoading(true);

    async function load(): Promise<void> {
      try {
        const fresh = (await medplum.readResource('Schedule', currentSchedule.id)) as WithId<Schedule>;
        const nextEntries =
          mode === 'general' ? readGeneralAvailability(fresh) : getEffectiveAvailabilityForWeek(fresh, weekStartDate);

        if (!cancelled) {
          setBlocks(templateToBlocks(nextEntries));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [medplum, mode, refreshKey, schedule, weekStartDate]);

  const refresh = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  const save = useCallback(
    async (nextBlocks: AvailabilityBlock[]): Promise<void> => {
      if (!schedule || (!providerRoleCode && !isAdmin)) {
        throw new Error('Provider role is required to save availability.');
      }

      setSaving(true);
      try {
        const fresh = (await medplum.readResource('Schedule', schedule.id)) as WithId<Schedule>;
        const nextEntries = blocksToTemplate(nextBlocks);
        const updatedSchedule =
          mode === 'general'
            ? setGeneralAvailability(fresh, nextEntries)
            : setSpecificAvailabilityOverride(fresh, weekStartDate, nextEntries);

        await medplum.updateResource(updatedSchedule);

        const eligibleAppointmentTypes = providerRoleCode
          ? appointmentTypes.filter((type) => type.providerTypes.includes(providerRoleCode))
          : appointmentTypes;
        const weekStartIso = new Date(weekStartTime).toISOString();
        const weekEndIso = new Date(weekStartTime + 6 * 24 * 60 * 60 * 1000).toISOString();

        await Promise.all(
          eligibleAppointmentTypes.map((appointmentType) =>
            reconcileScheduleAppointmentTypeAvailability(medplum, {
              schedule: updatedSchedule,
              timezone: scheduleTimezone,
              startDate: weekStartIso,
              endDate: weekEndIso,
              appointmentType,
            })
          )
        );

        refresh();
      } finally {
        setSaving(false);
      }
    },
    [
      appointmentTypes,
      isAdmin,
      medplum,
      mode,
      providerRoleCode,
      refresh,
      schedule,
      scheduleTimezone,
      weekStartDate,
      weekStartTime,
    ]
  );

  return { blocks, loading, refresh, save, saving };
}
